import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { onSignOut } from '../lib/signOutReset';
import { useAuthStore } from './authStore';
import { DEFAULT_CURRENCY, formatCurrency as formatCurrencyUtil, getCurrency } from '../constants/currencies';
import { DEFAULT_EXPENSE_CATEGORIES, DEFAULT_INCOME_CATEGORIES } from '../constants/financeCategories';
import type { FinanceXPEvent } from '../types/ecological';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceTransaction,
  FinanceBudget,
  FinanceBudgetPeriod,
  FinanceGoal,
  FinanceRecurringTransaction,
  FinanceSettings,
  TransactionWithRelations,
  BudgetWithSpending,
  CategorySpending,
  PeriodSummary
} from '../types/finance';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, isWithinInterval, parseISO, format, differenceInDays } from 'date-fns';

interface FinanceState {
  accounts: FinanceAccount[];
  categories: FinanceCategory[];
  transactions: FinanceTransaction[];
  budgets: FinanceBudget[];
  budgetPeriods: FinanceBudgetPeriod[];
  goals: FinanceGoal[];
  recurringTransactions: FinanceRecurringTransaction[];
  settings: FinanceSettings | null;
  isLoading: boolean;
  error: string | null;

  loadFinanceData: (userId: string) => Promise<void>;
  initializeUserFinance: (userId: string) => Promise<void>;

  createAccount: (account: Omit<FinanceAccount, 'id' | 'created_at' | 'updated_at'>) => Promise<FinanceAccount | null>;
  updateAccount: (id: string, updates: Partial<FinanceAccount>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;

  createCategory: (category: Omit<FinanceCategory, 'id' | 'created_at'>) => Promise<FinanceCategory | null>;
  updateCategory: (id: string, updates: Partial<FinanceCategory>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  createTransaction: (transaction: Omit<FinanceTransaction, 'id' | 'created_at' | 'updated_at'>) => Promise<FinanceTransaction | null>;
  updateTransaction: (id: string, updates: Partial<FinanceTransaction>) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;

  createBudget: (budget: Omit<FinanceBudget, 'id' | 'created_at' | 'updated_at'>) => Promise<FinanceBudget | null>;
  updateBudget: (id: string, updates: Partial<FinanceBudget>) => Promise<void>;
  deleteBudget: (id: string) => Promise<void>;

  createGoal: (goal: Omit<FinanceGoal, 'id' | 'created_at' | 'updated_at'>) => Promise<FinanceGoal | null>;
  updateGoal: (id: string, updates: Partial<FinanceGoal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  addToGoal: (id: string, amount: number) => Promise<void>;

  updateSettings: (updates: Partial<FinanceSettings>) => Promise<void>;

  getTransactionsWithRelations: () => TransactionWithRelations[];
  getBudgetsWithSpending: () => BudgetWithSpending[];
  getCategorySpending: (startDate: string, endDate: string) => CategorySpending[];
  getPeriodSummary: (startDate: string, endDate: string) => PeriodSummary;
  getTotalBalance: () => { amount: number; currency: string };
  formatAmount: (amount: number, currencyCode?: string) => string;

  getCurrentBudgetPeriod: (budget: FinanceBudget) => { start: Date; end: Date };
  getNextBudgetPeriod: (budget: FinanceBudget) => { start: Date; end: Date };
  getPreviousBudgetPeriod: (budget: FinanceBudget) => { start: Date; end: Date };

  awardFinanceXP: (event: FinanceXPEvent) => Promise<void>;
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: string) {
  if (at === epoch) useFinanceStore.setState({ error });
}

export const useFinanceStore = create<FinanceState>((set, get) => ({
  accounts: [],
  categories: [],
  transactions: [],
  budgets: [],
  budgetPeriods: [],
  goals: [],
  recurringTransactions: [],
  settings: null,
  isLoading: false,
  error: null,

  loadFinanceData: async (userId: string) => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const [
        { data: accounts },
        { data: categories },
        { data: transactions },
        { data: budgets },
        { data: budgetPeriods },
        { data: goals },
        { data: recurringTransactions },
        { data: settings }
      ] = await Promise.all([
        supabase.from('finance_accounts').select('*').eq('user_id', userId).order('sort_order'),
        supabase.from('finance_categories').select('*').eq('user_id', userId).order('sort_order'),
        supabase.from('finance_transactions').select('*').eq('user_id', userId).order('date', { ascending: false }),
        supabase.from('finance_budgets').select('*').eq('user_id', userId),
        supabase.from('finance_budget_periods').select('*').eq('user_id', userId),
        supabase.from('finance_goals').select('*').eq('user_id', userId),
        supabase.from('finance_recurring_transactions').select('*').eq('user_id', userId),
        supabase.from('finance_settings').select('*').eq('user_id', userId).maybeSingle()
      ]);
      // Signed out while it read: the next account must not get this one's money.
      if (at !== epoch) return;

      set({
        accounts: accounts || [],
        categories: categories || [],
        transactions: transactions || [],
        budgets: budgets || [],
        budgetPeriods: budgetPeriods || [],
        goals: goals || [],
        recurringTransactions: recurringTransactions || [],
        settings: settings || null,
        isLoading: false
      });
    } catch (error) {
      if (at === epoch) set({ error: 'Failed to load finance data', isLoading: false });
    }
  },

  initializeUserFinance: async (userId: string) => {
    /* ⚠️⚠️ ONLY FOR THE ACCOUNT SIGNED IN NOW. The pages call this after their load
       (`loadFinanceData(id).then(…)`): a load dropped by a sign-out still resolves, and
       the emptied store reads as "no settings, no categories" — it would seed for the
       account that left and then re-read it into the next account's store. */
    if (useAuthStore.getState().user?.id !== userId) return;
    const at = epoch;
    const { settings, categories } = get();

    if (!settings) {
      await supabase.from('finance_settings').insert({
        user_id: userId,
        preferred_currency: DEFAULT_CURRENCY,
        week_start_day: 0,
        fiscal_month_start_day: 1
      });
    }

    if (categories.length === 0) {
      const allCategories = [...DEFAULT_EXPENSE_CATEGORIES, ...DEFAULT_INCOME_CATEGORIES];
      const categoriesToInsert = allCategories.map((cat, index) => ({
        user_id: userId,
        name: cat.name,
        icon: cat.icon,
        color: cat.color,
        category_type: cat.type,
        is_system: true,
        sort_order: index
      }));

      await supabase.from('finance_categories').insert(categoriesToInsert);
    }

    // Signed out while it seeded: its re-read would answer the next account's store with nothing.
    if (at !== epoch) return;
    await get().loadFinanceData(userId);
  },

  createAccount: async (account) => {
    const at = epoch;
    const { data, error } = await supabase
      .from('finance_accounts')
      .insert(account)
      .select()
      .single();

    if (error) {
      failed(at, 'Failed to create account');
      return null;
    }

    // Saved for the account that signed out meanwhile — not shown to the next one.
    if (at !== epoch) return data;
    set(state => ({ accounts: [...state.accounts, data] }));
    return data;
  },

  updateAccount: async (id, updates) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_accounts')
      .update(updates)
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to update account');
      return;
    }

    set(state => ({
      accounts: state.accounts.map(a => a.id === id ? { ...a, ...updates } : a)
    }));
  },

  deleteAccount: async (id) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_accounts')
      .delete()
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to delete account');
      return;
    }

    set(state => ({
      accounts: state.accounts.filter(a => a.id !== id)
    }));
  },

  createCategory: async (category) => {
    const at = epoch;
    const { data, error } = await supabase
      .from('finance_categories')
      .insert(category)
      .select()
      .single();

    if (error) {
      failed(at, 'Failed to create category');
      return null;
    }

    // Saved for the account that signed out meanwhile — not shown to the next one.
    if (at !== epoch) return data;
    set(state => ({ categories: [...state.categories, data] }));
    return data;
  },

  updateCategory: async (id, updates) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_categories')
      .update(updates)
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to update category');
      return;
    }

    set(state => ({
      categories: state.categories.map(c => c.id === id ? { ...c, ...updates } : c)
    }));
  },

  deleteCategory: async (id) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_categories')
      .delete()
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to delete category');
      return;
    }

    set(state => ({
      categories: state.categories.filter(c => c.id !== id)
    }));
  },

  createTransaction: async (transaction) => {
    const at = epoch;
    const { data, error } = await supabase
      .from('finance_transactions')
      .insert(transaction)
      .select()
      .single();

    if (error) {
      failed(at, 'Failed to create transaction');
      return null;
    }

    const account = get().accounts.find(a => a.id === transaction.account_id);
    if (account) {
      const balanceChange = transaction.transaction_type === 'income'
        ? transaction.amount
        : transaction.transaction_type === 'expense'
          ? -transaction.amount
          : -transaction.amount;

      await get().updateAccount(account.id, {
        balance: account.balance + balanceChange
      });

      if (transaction.transaction_type === 'transfer' && transaction.transfer_to_account_id) {
        const toAccount = get().accounts.find(a => a.id === transaction.transfer_to_account_id);
        if (toAccount) {
          await get().updateAccount(toAccount.id, {
            balance: toAccount.balance + transaction.amount
          });
        }
      }
    }

    // Saved for the account that signed out meanwhile — not shown to the next one. (Its
    // accounts are gone from the store, so no balance above moved after the sign-out.)
    if (at !== epoch) return data;
    set(state => ({
      transactions: [data, ...state.transactions]
    }));

    get().awardFinanceXP({ type: 'transaction_logged' });

    return data;
  },

  updateTransaction: async (id, updates) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_transactions')
      .update(updates)
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to update transaction');
      return;
    }

    set(state => ({
      transactions: state.transactions.map(t => t.id === id ? { ...t, ...updates } : t)
    }));
  },

  deleteTransaction: async (id) => {
    const at = epoch;
    const transaction = get().transactions.find(t => t.id === id);

    const { error } = await supabase
      .from('finance_transactions')
      .delete()
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to delete transaction');
      return;
    }

    if (transaction) {
      const account = get().accounts.find(a => a.id === transaction.account_id);
      if (account) {
        const balanceChange = transaction.transaction_type === 'income'
          ? -transaction.amount
          : transaction.transaction_type === 'expense'
            ? transaction.amount
            : transaction.amount;

        await get().updateAccount(account.id, {
          balance: account.balance + balanceChange
        });

        if (transaction.transaction_type === 'transfer' && transaction.transfer_to_account_id) {
          const toAccount = get().accounts.find(a => a.id === transaction.transfer_to_account_id);
          if (toAccount) {
            await get().updateAccount(toAccount.id, {
              balance: toAccount.balance - transaction.amount
            });
          }
        }
      }
    }

    set(state => ({
      transactions: state.transactions.filter(t => t.id !== id)
    }));
  },

  createBudget: async (budget) => {
    const at = epoch;
    const { data, error } = await supabase
      .from('finance_budgets')
      .insert(budget)
      .select()
      .single();

    if (error) {
      failed(at, 'Failed to create budget');
      return null;
    }

    // Saved for the account that signed out meanwhile — not shown to the next one.
    if (at !== epoch) return data;
    set(state => ({ budgets: [...state.budgets, data] }));

    get().awardFinanceXP({ type: 'budget_created' });

    return data;
  },

  updateBudget: async (id, updates) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_budgets')
      .update(updates)
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to update budget');
      return;
    }

    set(state => ({
      budgets: state.budgets.map(b => b.id === id ? { ...b, ...updates } : b)
    }));
  },

  deleteBudget: async (id) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_budgets')
      .delete()
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to delete budget');
      return;
    }

    set(state => ({
      budgets: state.budgets.filter(b => b.id !== id)
    }));
  },

  createGoal: async (goal) => {
    const at = epoch;
    const { data, error } = await supabase
      .from('finance_goals')
      .insert(goal)
      .select()
      .single();

    if (error) {
      failed(at, 'Failed to create goal');
      return null;
    }

    // Saved for the account that signed out meanwhile — not shown to the next one.
    if (at !== epoch) return data;
    set(state => ({ goals: [...state.goals, data] }));

    get().awardFinanceXP({ type: 'budget_created', description: `Created savings goal: ${goal.name || 'New goal'}` });

    return data;
  },

  updateGoal: async (id, updates) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_goals')
      .update(updates)
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to update goal');
      return;
    }

    set(state => ({
      goals: state.goals.map(g => g.id === id ? { ...g, ...updates } : g)
    }));
  },

  deleteGoal: async (id) => {
    const at = epoch;
    const { error } = await supabase
      .from('finance_goals')
      .delete()
      .eq('id', id);

    if (error) {
      failed(at, 'Failed to delete goal');
      return;
    }

    set(state => ({
      goals: state.goals.filter(g => g.id !== id)
    }));
  },

  addToGoal: async (id, amount) => {
    const goal = get().goals.find(g => g.id === id);
    if (!goal) return;

    const previousAmount = goal.current_amount;
    const newAmount = goal.current_amount + amount;
    const status = newAmount >= goal.target_amount ? 'completed' : goal.status;

    await get().updateGoal(id, {
      current_amount: newAmount,
      status
    });

    const previousPercent = (previousAmount / goal.target_amount) * 100;
    const newPercent = (newAmount / goal.target_amount) * 100;

    const milestones = [25, 50, 75, 100];
    for (const milestone of milestones) {
      if (previousPercent < milestone && newPercent >= milestone) {
        if (milestone === 100) {
          get().awardFinanceXP({
            type: 'goal_completed',
            amount: goal.target_amount,
            description: `Completed goal: ${goal.name}`
          });
        } else {
          get().awardFinanceXP({
            type: 'savings_milestone',
            amount: milestone,
            description: `Reached ${milestone}% of ${goal.name}`
          });
        }
        break;
      }
    }
  },

  updateSettings: async (updates) => {
    const at = epoch;
    const { settings } = get();
    if (!settings) return;

    const { error } = await supabase
      .from('finance_settings')
      .update(updates)
      .eq('user_id', settings.user_id);

    if (error) {
      failed(at, 'Failed to update settings');
      return;
    }

    set(state => ({
      settings: state.settings ? { ...state.settings, ...updates } : null
    }));
  },

  getTransactionsWithRelations: () => {
    const { transactions, accounts, categories } = get();
    return transactions.map(t => ({
      ...t,
      account: accounts.find(a => a.id === t.account_id),
      category: t.category_id ? categories.find(c => c.id === t.category_id) : undefined,
      transfer_account: t.transfer_to_account_id
        ? accounts.find(a => a.id === t.transfer_to_account_id)
        : undefined
    }));
  },

  getBudgetsWithSpending: () => {
    const { budgets, categories, transactions } = get();
    const today = new Date();

    return budgets.map(budget => {
      const category = categories.find(c => c.id === budget.category_id);
      const period = get().getCurrentBudgetPeriod(budget);

      const periodTransactions = transactions.filter(t => {
        if (t.category_id !== budget.category_id) return false;
        if (t.transaction_type !== 'expense') return false;
        const txDate = parseISO(t.date);
        return isWithinInterval(txDate, { start: period.start, end: period.end });
      });

      const spent = periodTransactions.reduce((sum, t) => sum + t.amount, 0);
      const remaining = budget.budgeted_amount - spent;
      const percentage = budget.budgeted_amount > 0
        ? (spent / budget.budgeted_amount) * 100
        : 0;

      return {
        ...budget,
        category,
        current_period: {
          id: `${budget.id}-current`,
          user_id: budget.user_id,
          budget_id: budget.id,
          period_start: format(period.start, 'yyyy-MM-dd'),
          period_end: format(period.end, 'yyyy-MM-dd'),
          budgeted_amount: budget.budgeted_amount,
          status: 'active' as const,
          created_at: budget.created_at
        },
        spent_amount: spent,
        remaining_amount: remaining,
        percentage_used: Math.min(percentage, 100)
      };
    });
  },

  getCategorySpending: (startDate, endDate) => {
    const { transactions, categories } = get();
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    const expenseTransactions = transactions.filter(t => {
      if (t.transaction_type !== 'expense') return false;
      const txDate = parseISO(t.date);
      return isWithinInterval(txDate, { start, end });
    });

    const categoryTotals = new Map<string, number>();
    let totalExpenses = 0;

    expenseTransactions.forEach(t => {
      const catId = t.category_id || 'uncategorized';
      categoryTotals.set(catId, (categoryTotals.get(catId) || 0) + t.amount);
      totalExpenses += t.amount;
    });

    const spending: CategorySpending[] = [];
    categoryTotals.forEach((amount, categoryId) => {
      const category = categories.find(c => c.id === categoryId);
      spending.push({
        category_id: categoryId,
        category_name: category?.name || 'Uncategorized',
        category_icon: category?.icon || 'HelpCircle',
        category_color: category?.color || '#64748b',
        total_amount: amount,
        transaction_count: expenseTransactions.filter(t => (t.category_id || 'uncategorized') === categoryId).length,
        percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0
      });
    });

    return spending.sort((a, b) => b.total_amount - a.total_amount);
  },

  getPeriodSummary: (startDate, endDate) => {
    const { transactions } = get();
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    const periodTransactions = transactions.filter(t => {
      const txDate = parseISO(t.date);
      return isWithinInterval(txDate, { start, end });
    });

    const totalIncome = periodTransactions
      .filter(t => t.transaction_type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = periodTransactions
      .filter(t => t.transaction_type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    return {
      period_start: startDate,
      period_end: endDate,
      total_income: totalIncome,
      total_expenses: totalExpenses,
      net_amount: totalIncome - totalExpenses,
      top_categories: get().getCategorySpending(startDate, endDate).slice(0, 5)
    };
  },

  getTotalBalance: () => {
    const { accounts, settings } = get();
    const currency = settings?.preferred_currency || DEFAULT_CURRENCY;

    const total = accounts
      .filter(a => a.is_active && a.include_in_total)
      .reduce((sum, a) => sum + a.balance, 0);

    return { amount: total, currency };
  },

  formatAmount: (amount, currencyCode) => {
    const { settings } = get();
    const code = currencyCode || settings?.preferred_currency || DEFAULT_CURRENCY;
    return formatCurrencyUtil(amount, code);
  },

  getCurrentBudgetPeriod: (budget) => {
    const today = new Date();
    const weekStartDay = budget.week_start_day;
    const monthStartDay = budget.month_start_day;

    switch (budget.period_type) {
      case 'weekly': {
        const start = startOfWeek(today, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
        const end = endOfWeek(today, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
        return { start, end };
      }
      case 'biweekly': {
        const weekStart = startOfWeek(today, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
        const weekNumber = Math.floor(differenceInDays(weekStart, new Date(2024, 0, 1)) / 7);
        const isEvenWeek = weekNumber % 2 === 0;
        const start = isEvenWeek ? weekStart : addDays(weekStart, -7);
        const end = addDays(start, 13);
        return { start, end };
      }
      case 'monthly': {
        let start: Date;
        let end: Date;

        if (today.getDate() >= monthStartDay) {
          start = new Date(today.getFullYear(), today.getMonth(), monthStartDay);
          end = new Date(today.getFullYear(), today.getMonth() + 1, monthStartDay - 1);
        } else {
          start = new Date(today.getFullYear(), today.getMonth() - 1, monthStartDay);
          end = new Date(today.getFullYear(), today.getMonth(), monthStartDay - 1);
        }
        return { start, end };
      }
      case 'custom': {
        if (budget.custom_start_date && budget.custom_end_date) {
          return {
            start: parseISO(budget.custom_start_date),
            end: parseISO(budget.custom_end_date)
          };
        }
        return { start: startOfMonth(today), end: endOfMonth(today) };
      }
      default:
        return { start: startOfMonth(today), end: endOfMonth(today) };
    }
  },

  getNextBudgetPeriod: (budget) => {
    const current = get().getCurrentBudgetPeriod(budget);

    switch (budget.period_type) {
      case 'weekly':
        return {
          start: addWeeks(current.start, 1),
          end: addWeeks(current.end, 1)
        };
      case 'biweekly':
        return {
          start: addWeeks(current.start, 2),
          end: addWeeks(current.end, 2)
        };
      case 'monthly':
        return {
          start: addMonths(current.start, 1),
          end: addMonths(current.end, 1)
        };
      case 'custom': {
        const duration = differenceInDays(current.end, current.start);
        return {
          start: addDays(current.end, 1),
          end: addDays(current.end, duration + 1)
        };
      }
      default:
        return {
          start: addMonths(current.start, 1),
          end: addMonths(current.end, 1)
        };
    }
  },

  getPreviousBudgetPeriod: (budget) => {
    const current = get().getCurrentBudgetPeriod(budget);

    switch (budget.period_type) {
      case 'weekly':
        return {
          start: addWeeks(current.start, -1),
          end: addWeeks(current.end, -1)
        };
      case 'biweekly':
        return {
          start: addWeeks(current.start, -2),
          end: addWeeks(current.end, -2)
        };
      case 'monthly':
        return {
          start: addMonths(current.start, -1),
          end: addMonths(current.end, -1)
        };
      case 'custom': {
        const duration = differenceInDays(current.end, current.start);
        return {
          start: addDays(current.start, -(duration + 1)),
          end: addDays(current.start, -1)
        };
      }
      default:
        return {
          start: addMonths(current.start, -1),
          end: addMonths(current.end, -1)
        };
    }
  },

  awardFinanceXP: async (_event: FinanceXPEvent) => {
    // EC3: finance XP is now a DERIVED view (utils/financeXp). The ecological
    // domain (useUserStats) and the Financial Stewardship area (useDisciplines)
    // read it straight from the finance data, so nothing is minted here — the old
    // path wrote the now-retired ecologicalAreasStore.
  },

  /**
   * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
   * not reload the page: the next account saw the last one's accounts, transactions and
   * budgets. An update or delete still in flight maps by id over the emptied lists.
   */
  clearStore: () => {
    epoch += 1;
    set({
      accounts: [], categories: [], transactions: [], budgets: [], budgetPeriods: [], goals: [],
      recurringTransactions: [], settings: null, isLoading: false, error: null,
    });
  },
}));

onSignOut(() => useFinanceStore.getState().clearStore());
